import { API } from "aws-amplify";
import { API_NAMES } from "../../utils";
import {
  AudienceData,
  CampaignIntent,
  CreateMarketingAdsetRequest,
  CreateMarketingCampaignRequest,
  UpdateAdCreativeRequest,
  UpdateMarketingCampaign,
} from "@common/interfaces";
import type {
  GuidedEditPayload,
  GuidedEditUpdateRequestBody,
  LaunchCampaignRequestBody,
} from "common/interfaces/adsManagerGuidedEdit";
import type {
  BatchIntentApplyApiResponse,
  BatchIntentApplyOperation,
} from "src/Manager/Campaigns/Ads/batchIntentApplyTypes";

const fetchQueryParams = () => {
  if (window && window.location && window.location.search) {
    const params = new URLSearchParams(window.location.search);
    params.delete("update_id");
    const obj = Object.fromEntries(params);

    return obj;
  }
};

const formatError = (err: any = {}) => {
  console.log(err.response);
  const { response = {} } = err;
  const { data = {}, status } = response;
  const { response: serverResponse = {} } = data;

  return {
    statusCode: status || 500,
    message: serverResponse.message || "An unknown error occured",
  };
};

const admin_updateCampaign = async ({
  id,
  event_ids,
  ...body
}: UpdateMarketingCampaign) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/ads/campaigns/${id}`,
      {
        queryStringParameters,
        body: { ...body, event_ids },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createCampaign = async ({
  event_ids,
  ...body
}: CreateMarketingCampaignRequest) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ADMIN, `/ads/campaigns`, {
      queryStringParameters,
      body: { ...body, event_ids },
    });

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_previewAd = async ({
  adset_id,
  campaign_id,
  width,
  height,
}: {
  adset_id: string;
  campaign_id: string;
  width?: number;
  height?: number;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/adsets/${adset_id}/preview`,
      {
        queryStringParameters,
        body: {
          // // INSTAGRAM_STORY or INSTAGRAM_STANDARD
          // ad_format: "INSTAGRAM_STANDARD",
          width,
          height,
        },
      }
    );

    console.log(response);
    return response as {
      previews: {
        format: string;
        name: string;
        preview: {
          body: string;
        };
      }[];
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateAdCreative = async ({
  campaign_id,
  parent_id,
  ...body
}: UpdateAdCreativeRequest) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/adsets/${parent_id}/adcreatives`,
      {
        queryStringParameters,
        body,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createAdset = async ({
  parent_id,
  ...body
}: CreateMarketingAdsetRequest) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${parent_id}/adsets`,
      {
        queryStringParameters,
        body: {
          ...body,
          parent_id,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateAdset = async ({
  parent_id,
  id,
  ...body
}: CreateMarketingAdsetRequest) => {
  try {
    console.log({
      parent_id,
      id,
      body,
    });
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/ads/${parent_id}/adsets/${id}`,
      {
        queryStringParameters,
        body: {
          ...body,
          parent_id,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_duplicateAdset = async ({
  campaign_id,
  adset_id,
}: {
  campaign_id: string;
  adset_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/adsets/${adset_id}/duplicate`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteCreative = async ({
  campaign_id,
  adset_id,
  creative_id,
}: {
  campaign_id: string;
  adset_id: string;
  creative_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/adsets/${adset_id}/creatives/${creative_id}`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteAdset = async ({
  campaign_id,
  adset_id,
}: {
  campaign_id: string;
  adset_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/adsets/${adset_id}`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listCampaigns = async () => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/ads/campaigns`, {
      queryStringParameters,
    });

    console.log(response);
    return {
      campaigns: Array.isArray((response as any)?.campaigns)
        ? (response as any).campaigns
        : Array.isArray((response as any)?.response?.campaigns)
          ? (response as any).response.campaigns
          : [],
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchCampaign = async (
  campaign_id: string,
  {
    include_event = false,
    // include_llm_intents = false,
  }: {
    include_event?: boolean;
    // include_llm_intents?: boolean;
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const params = new URLSearchParams({
      ...queryStringParameters,
      include_event: include_event ? "true" : "false",
    });
    const query = params.toString();
    const path = query
      ? `/ads/campaigns/${campaign_id}?${query}`
      : `/ads/campaigns/${campaign_id}`;
    const { response } = await API.get(API_NAMES.ADMIN, path, {});

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

/** Org-level ads metrics snapshot (from marketing table, written when ads report is built). */
export interface CampaignMetricsSnapshot {
  spend: number;
  roas: number;
  conversions: number;
  /** Average CTR (spend-weighted), e.g. 1.25 for 1.25%. */
  ctr?: number;
  updated_at: string;
}

/** Fetches org-level aggregated ads metrics and suggested intents. Used for Meta Campaigns summary cards and Proactive Insights. */
const admin_fetchOrganizationAdsMetrics = async (): Promise<{
  metrics: CampaignMetricsSnapshot | null;
  suggested_intents?: CampaignIntent[];
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const params = queryStringParameters
      ? new URLSearchParams(queryStringParameters)
      : undefined;
    const path = params ? `/ads/metrics?${params.toString()}` : "/ads/metrics";
    const { response } = await API.get(API_NAMES.ADMIN, path, {});
    return response as {
      metrics: CampaignMetricsSnapshot | null;
      suggested_intents?: CampaignIntent[];
    };
  } catch (err: any) {
    if (err?.response?.status === 404) return { metrics: null };
    console.error(err);
    throw formatError(err);
  }
};

const admin_checkCampaignUpdate = async (
  campaign_id: string,
  update_id: string
): Promise<{
  status: string;
  update_status: string;
  id: string;
  date: string;
  request: any;
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/ads/campaigns/${campaign_id}/check`,
      {
        queryStringParameters: {
          ...queryStringParameters,
          update_id,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_ignoreCampaignIntent = async (
  campaign_id: string,
  intents: CampaignIntent[]
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/campaigns/${campaign_id}/intents/ignore`,
      {
        queryStringParameters,
        body: { intents },
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

/** Batch apply intents: one request with operations = [{ campaign_id, intent_ids }, ...]. */
const admin_batchApplyCampaignIntents = async (
  operations: BatchIntentApplyOperation[]
): Promise<BatchIntentApplyApiResponse> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      "/ads/intents/batch-apply",
      {
        queryStringParameters,
        body: { operations },
      }
    );
    return response as BatchIntentApplyApiResponse;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

/** Batch ignore intents: one request with operations = [{ campaign_id, intent_ids }, ...]. */
const admin_batchIgnoreCampaignIntents = async (
  operations: Array<{
    campaign_id: string;
    intent_ids: string[];
  }>
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      "/ads/intents/batch-ignore",
      {
        queryStringParameters,
        body: { operations },
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_runCampaignIntents = async (campaign_id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/campaigns/${campaign_id}/intents/run`,
      {
        queryStringParameters,
      }
    );
    return response as {
      execution_id: string;
      execution_arn: string;
      status: string;
      message?: string;
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listPixels = async ({
  ad_account_id,
}: {
  ad_account_id: string;
}) => {
  try {
    const { response } = await API.get(API_NAMES.ADMIN, `/ads/pixels`, {
      queryStringParameters: {
        ad_account_id,
      },
    });

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};
const admin_listInstagramAccounts = async ({
  ad_account_id,
  facebook_page_id,
}: {
  ad_account_id: string;
  facebook_page_id?: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/ads/instagram_accounts`,
      {
        queryStringParameters: {
          ...queryStringParameters,
          ad_account_id,
          ...(facebook_page_id ? { facebook_page_id } : {}),
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listPages = async ({
  ad_account_id,
}: {
  ad_account_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/ads/pages`, {
      queryStringParameters: {
        ...queryStringParameters,
        ad_account_id,
      },
    });

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listAdAccounts = async () => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/ads/accounts`, {
      queryStringParameters,
    });

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listCustomConversions = async ({
  ad_account_id,
}: {
  ad_account_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/ads/custom_conversions`,
      {
        queryStringParameters: {
          ...queryStringParameters,
          ad_account_id,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteCustomConversions = async ({
  ad_account_id,
  custom_conversion_ids,
}: {
  ad_account_id: string;
  custom_conversion_ids: string[];
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/custom_conversions/delete`,
      {
        queryStringParameters: {
          ...queryStringParameters,
          ad_account_id,
        },
        body: { custom_conversion_ids },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_searchAudiences = async ({
  query,
  ad_account_id,
  limit,
}: {
  query: string;
  ad_account_id: string;
  limit?: number;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/search/custom-audiences`,
      {
        queryStringParameters,
        body: { query, ad_account_id, limit },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_listAudiences = async ({
  ad_account_id,
  limit,
}: {
  ad_account_id: string;
  limit?: number;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/ads/audiences`, {
      queryStringParameters: {
        ...queryStringParameters,
        ad_account_id,
        limit,
      },
    });

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};
const admin_searchInterests = async ({ query }: { query: string }) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/search/interests`,
      {
        queryStringParameters,
        body: { query },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_searchGeoMarkets = async ({ query }: { query: string }) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/search/locations`,
      {
        queryStringParameters,
        body: { query },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_unpublishCampaign = async ({
  campaign_id,
}: {
  campaign_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/unpublish`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_previewImportCampaignFromMeta = async ({
  campaign_id,
  import_mode = "match",
}: {
  campaign_id: string;
  import_mode?: "match" | "replace_from_meta";
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/import-from-meta/preview`,
      {
        queryStringParameters,
        body: { import_mode },
      }
    );

    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_applyImportCampaignFromMeta = async ({
  campaign_id,
  meta_adset_id_to_kyd_adset_id,
  meta_updated_time_if_unchanged,
  fill_missing_meta_adsets_with_new_kyd_ids = false,
  tombstone_superseded_local_adsets = false,
  persist = true,
}: {
  campaign_id: string;
  meta_adset_id_to_kyd_adset_id: Record<string, string>;
  meta_updated_time_if_unchanged?: string;
  fill_missing_meta_adsets_with_new_kyd_ids?: boolean;
  tombstone_superseded_local_adsets?: boolean;
  persist?: boolean;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/import-from-meta/apply`,
      {
        queryStringParameters,
        body: {
          meta_adset_id_to_kyd_adset_id,
          meta_updated_time_if_unchanged,
          fill_missing_meta_adsets_with_new_kyd_ids,
          tombstone_superseded_local_adsets,
          persist,
        },
      }
    );

    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_publishCampaign = async ({
  campaign_id,
  publish_all_adsets = false,
}: {
  campaign_id: string;
  publish_all_adsets?: boolean;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/publish`,
      {
        queryStringParameters,
        body: {
          publish_all_adsets,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_publishAdset = async ({
  campaign_id,
  adset_id,
}: {
  campaign_id: string;
  adset_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/adsets/${adset_id}/publish`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_unpublishAdset = async ({
  campaign_id,
  adset_id,
}: {
  campaign_id: string;
  adset_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/adsets/${adset_id}/unpublish`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_unpublishAd = async ({
  campaign_id,
  adset_id,
  ad_id,
}: {
  campaign_id: string;
  adset_id: string;
  ad_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/adsets/${adset_id}/ads/${ad_id}/unpublish`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_publishAd = async ({
  campaign_id,
  adset_id,
  ad_id,
}: {
  campaign_id: string;
  adset_id: string;
  ad_id: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/${campaign_id}/adsets/${adset_id}/ads/${ad_id}/publish`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createOrganizationAccess = async ({
  code,
  access_token,
}: {
  code?: string;
  access_token?: string;
}) => {
  try {
    const { response } = await API.post(API_NAMES.ADMIN, `/ads/access`, {
      body: {
        code,
        access_token,
      },
    });

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_removeOrganizationAccess = async () => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(API_NAMES.ADMIN, `/ads/access`, {
      queryStringParameters,
    });

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_launchCampaign = async ({
  event_id,
  ad_account_id,
  budget,
  schedule,
  creatives,
  targeting,
  pixel_id,
}: LaunchCampaignRequestBody) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const body: Record<string, unknown> = {
      event_id,
      ad_account_id,
      budget,
      schedule,
      creatives,
      targeting,
    };
    if (pixel_id) body.pixel_id = pixel_id;
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/campaigns/launch`,
      {
        queryStringParameters,
        body,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchCampaignDefaults = async ({
  event_id,
  ad_account_id,
}: {
  event_id: string;
  ad_account_id?: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const body: Record<string, unknown> = {
      event_id,
    };
    if (ad_account_id) body.ad_account_id = ad_account_id;
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/ads/campaigns/defaults`,
      {
        queryStringParameters,
        body,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

/** GET guided-edit: shared wire + error handling. */
const admin_getGuidedEditPayload = async (
  path: string
): Promise<GuidedEditPayload> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, path, {
      queryStringParameters,
    });
    return response as GuidedEditPayload;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

/** GET `.../campaigns/:id/guided-edit` — server picks default ad set anchor. */
const admin_fetchGuidedEditPayloadForCampaign = async ({
  campaign_id,
}: {
  campaign_id: string;
}): Promise<GuidedEditPayload> =>
  admin_getGuidedEditPayload(`/ads/campaigns/${campaign_id}/guided-edit`);

/** GET `.../campaigns/:id/adsets/:adsetId/guided-edit` — explicit ad set anchor. */
const admin_fetchGuidedEditPayloadForAdset = async ({
  campaign_id,
  adset_id,
}: {
  campaign_id: string;
  adset_id: string;
}): Promise<GuidedEditPayload> => {
  const id = String(adset_id).trim();
  if (!id) {
    throw new Error("fetchGuidedEditPayloadForAdset: adset_id is required");
  }
  return admin_getGuidedEditPayload(
    `/ads/campaigns/${campaign_id}/adsets/${encodeURIComponent(id)}/guided-edit`
  );
};

const admin_updateGuidedEditPayload = async ({
  campaign_id,
  adset_id,
  ...body
}: {
  campaign_id: string;
  adset_id: string;
} & GuidedEditUpdateRequestBody) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/ads/campaigns/${campaign_id}/adsets/${adset_id}/guided-edit`,
      {
        queryStringParameters,
        body,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

export const ADS_ACTIONS = {
  createCampaign: admin_createCampaign,
  createAdset: admin_createAdset,
  updateAdset: admin_updateAdset,
  listPixels: admin_listPixels,
  listPages: admin_listPages,
  listCustomConversions: admin_listCustomConversions,
  deleteCustomConversions: admin_deleteCustomConversions,
  listInstagramAccounts: admin_listInstagramAccounts,
  listCampaigns: admin_listCampaigns,
  previewAd: admin_previewAd,
  listAudiences: admin_listAudiences,
  searchInterests: admin_searchInterests,
  searchGeoMarkets: admin_searchGeoMarkets,
  publishCampaign: admin_publishCampaign,
  previewImportCampaignFromMeta: admin_previewImportCampaignFromMeta,
  applyImportCampaignFromMeta: admin_applyImportCampaignFromMeta,
  fetchCampaign: admin_fetchCampaign,
  fetchOrganizationAdsMetrics: admin_fetchOrganizationAdsMetrics,
  fetchCampaignDefaults: admin_fetchCampaignDefaults,
  fetchGuidedEditPayloadForCampaign: admin_fetchGuidedEditPayloadForCampaign,
  fetchGuidedEditPayloadForAdset: admin_fetchGuidedEditPayloadForAdset,
  updateGuidedEditPayload: admin_updateGuidedEditPayload,
  launchCampaign: admin_launchCampaign,
  publishAdset: admin_publishAdset,
  unpublishAdset: admin_unpublishAdset,
  unpublishAd: admin_unpublishAd,
  publishAd: admin_publishAd,
  listAdAccounts: admin_listAdAccounts,
  updateCampaign: admin_updateCampaign,
  checkCampaignUpdate: admin_checkCampaignUpdate,
  batchApplyCampaignIntents: admin_batchApplyCampaignIntents,
  batchIgnoreCampaignIntents: admin_batchIgnoreCampaignIntents,
  ignoreCampaignIntent: admin_ignoreCampaignIntent,
  runCampaignIntents: admin_runCampaignIntents,
  unpublishCampaign: admin_unpublishCampaign,
  updateAdCreative: admin_updateAdCreative,
  createOrganizationAccess: admin_createOrganizationAccess,
  removeOrganizationAccess: admin_removeOrganizationAccess,
  searchAudiences: admin_searchAudiences,
  duplicateAdset: admin_duplicateAdset,
  deleteAdset: admin_deleteAdset,
  deleteCreative: admin_deleteCreative,
};

export default ADS_ACTIONS;
